import { Api } from "../axios-config";

export interface IComission {
	id: string;
	invoice: string;
	partner: string;
	cycle: string;
	totalAmount: string;
	type: string;
	clientId: string;
	status: string;
	createdAt: string;
	updatedAt: string;
	totAmount: string;
	totValue: string;
	reference: string;
	dueDate: string;
	paymentDate: string;
	cancelDate: string;
	cancelReason: string;
	cancelUser: string;
	// Canal de pagamento à imobiliária ('livve' hoje, outro amanhã) — undefined até o fechamento
	// ser enviado a algum canal.
	gateway?: string;
}

// Grupo (cliente + plano + tipo) decidido numa rodada de fechamento — contrato confirmado pelo
// backend no manual v2.1.
export type CloseCycleGroup = {
	clientId: string;
	planId: string;
	type: string; // 'bonus' | 'prize'
	reference: string; // ciclo (yyyy-MM) gravado em settled_cycle para este grupo
	minGuarantees: number;
	activeGuarantees: number;
	items: number;
};

export type CloseCycleResult = {
	// null quando o fechamento foi por ids/clientIds sem reference — cada grupo carrega o próprio
	// reference nesse caso (pode até cruzar meses numa seleção de parcelas específicas).
	reference: string | null;
	opened: CloseCycleGroup[];
	notEligible: CloseCycleGroup[];
	skippedWithoutMeta: number;
	summary: string;
};

// Fechamento individual: usuário escolhe as parcelas específicas (ids de comission_values).
// Fechamento em lote: usuário seleciona imobiliárias inteiras — o backend apura todas as parcelas
// 'preview' vencidas daquelas imobiliárias. Ambos aceitam combinar com reference (yyyy-MM); sem
// nenhum dos três campos o backend responde 400.
export type CloseCyclePayload = { reference: string; clientId?: string } | { ids: string[] } | { clientIds: string[]; reference?: string };

export type TComissionTotalCount = {
	data: IComission[];
	totalCount: number;
};

export type ComissionDocumentType = {
	type: string;
	name: string;
	id: string;
	mimetype: string;
	filename: string;
	originalFilename: string;
	url: string;
	uploadedDate: string;
	size: number;
	docType: string;
};

export type ComissionStatusData = {
	id: string;
	comissionId: string;
	type: string;
	description: string;
	userId: string;
	dueDate: string;
	value: string;
	installmentValue: string;
	createdAt: string;
	updatedAt: string;
	planId: string;
	cancelDate: string;
	reason: string;
	status: string;
	proposalId: string;
	installmentId: string;
	clientId: string;
	paymentDate: string;
	clientPayment: string;
	proposal: { certificateNumber: number };
	// Ciclo em que o fechamento decidiu o destino do item (ex: "2026-09") — só presente em itens
	// que passaram pela apuração de meta, undefined pros demais.
	settledCycle?: string;
	// Número da fatura (Comission.reference) e canal de pagamento (Comission.gateway) do
	// cabeçalho ao qual este item pertence — undefined até existir fatura gerada.
	invoiceReference?: string;
	invoiceGateway?: string;
};

export type TComissionStatusTotalCount = {
	data: ComissionStatusData[];
	totalCount: number;
};

const getComission = async (page = 1, limit = 10, search = "", dataini = "", datafim = "", status = "", clientId = ""): Promise<TComissionTotalCount | Error> => {
	try {
		const { data } = await Api().get(`/comission?_page=${page}&_limit=${limit}&nome_like=${search}&dataini=${dataini}&datafim=${datafim}&status=${status}&clientId=${clientId}`);
		if (data.results) {
			return { data: data.results, totalCount: data.total };
		}
		return new Error("Erro ao buscar comissões");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao buscar comissões");
	}
};

const getComissionByID = async (id: string): Promise<IComission | Error> => {
	try {
		const { data } = await Api().get(`/comission/values/${id}`);
		if (data) {
			return data;
		}
		return new Error("Erro ao buscar valores");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao buscar valores");
	}
};

const getComissionDetail = async (id: string): Promise<IComission | Error> => {
	try {
		const { data } = await Api().get(`/comission/details/${id}`);
		if (data) {
			return data;
		}
		return new Error("Erro ao buscar valores");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao buscar valores");
	}
};

const addValues = async (values: any): Promise<IComission | Error> => {
	try {
		const { data } = await Api().post(`/comission/addvalues`, values);
		if (data) {
			return data;
		}
		return new Error("Erro ao adicionar valor");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao adicionar valor");
	}
};

const getComissionValues = async (id: string): Promise<IComission | Error> => {
	try {
		const { data } = await Api().get(`/comission/values/${id}`);
		if (data) {
			return data;
		}
		return new Error("Erro ao buscar valores ");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao buscar valores");
	}
};

const getComissionValuesDetail = async (id: string, dataini: string, datafim: string): Promise<any | Error> => {
	try {
		const { data } = await Api().get(`/comission/values/open?_page=1&_limit=1000&dataini=${dataini}&datafim=${datafim}&clientId=${id}&status=todos`);
		if (data) {
			return data;
		}
		return new Error("Erro ao buscar valores ");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao buscar valores");
	}
};

const uploadComissionDocument = async (comissionId: string, file: File): Promise<any> => {
	const formData = new FormData();
	formData.append("comissionId", comissionId);
	formData.append("documentTypeId", "comission_prize");
	formData.append("file", file);
	const { data } = await Api().post("/comission-documents", formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});

	return data as any;
};

const getComissionDocuments = async (comissionId: string): Promise<ComissionDocumentType[]> => {
	const { data } = await Api().get(`/comission-documents/comissions/${comissionId}`);
	return data as ComissionDocumentType[];
};

const removeDocumentByComission = async (comissionId: string): Promise<void | Error> => {
	try {
		const { data } = await Api().patch(`/proposal-documents/comissions/${comissionId}`);

		if (!data) return new Promise((_, reject) => reject(new Error("Erro ao remover documento.")));
	} catch (error: any) {
		console.error(error);
		return new Promise((_, reject) => reject(error));
	}
};

const getComissionPdf = async (comissionId: string): Promise<any> => {
	try {
		const { data } = await Api().get(`/comission/pdf/${comissionId}`, {
			responseType: "blob",
		});
		if (data) {
			const file = new Blob([data], { type: "application/pdf" });
			const fileURL = URL.createObjectURL(file);
			window.open(fileURL);
		}
		return new Promise((_, reject) => true);
	} catch (error) {
		console.error(error);
		return new Error((error as { message: string }).message || "Erro ao consultar o registro.");
	}
};

const cancelValuesComission = async (id: string): Promise<void | Error> => {
	try {
		const { data } = await Api().delete(`/comission/cancel-values/${id}`);
		console.log(data);

		if (!data) return new Promise((_, reject) => reject(new Error("Erro ao cancelar Valor.")));
		return data;
	} catch (error: any) {
		console.error(error);
		return new Promise((_, reject) => reject(error));
	}
};

const updateValuesComission = async (id: string, values: any): Promise<void | Error> => {
	try {
		const { data } = await Api().patch(`/comission/updatevalues/${id}`, values);
		console.log(data);

		if (!data) return new Promise((_, reject) => reject(new Error("Erro ao atualizar Valores.")));
		return data;
	} catch (error: any) {
		console.error(error);
		return new Promise((_, reject) => reject(error));
	}
};

// closeCycle dispara a apuração de elegibilidade das parcelas informadas — contrato confirmado
// pelo backend (manual v2.1): aceita reference/clientId, ids ou clientIds.
const closeCycle = async (payload: CloseCyclePayload): Promise<CloseCycleResult | Error> => {
	try {
		const { data } = await Api().post(`/comission/close-cycle`, payload);
		return data;
	} catch (error) {
		console.error(error);
		return new Error((error as { message: string }).message || "Erro ao fechar o ciclo.");
	}
};

export type UndoCloseCycleResult = { message: string; id: string; status: string };

// undoCloseCycle reverte 'open'/'not_eligible' de volta pra 'preview' — bloqueado assim que a
// parcela entra numa fatura enviada à Livve (livveOrderId/gateway preenchidos) ou já teve pontos
// creditados. Endpoint confirmado pelo backend (manual v2.1, seção 2.4).
const undoCloseCycle = async (id: string): Promise<UndoCloseCycleResult | Error> => {
	try {
		const { data } = await Api().post(`/comission/values/${id}/undo-close-cycle`);
		return data;
	} catch (error) {
		console.error(error);
		return new Error((error as { message: string }).message || "Erro ao desfazer o fechamento.");
	}
};

// ---------- Livve ----------

// Valores exigidos pelo backend no filtro `status` (manual v2.1, seção 2.5) — em inglês, diferente
// do resto do sistema. Tradução pra PT-BR fica só na camada de exibição (TableLivve).
export type LivveInvoiceStatus = "ready" | "sent" | "paid";

export type LivveInvoiceRow = {
	id: string; // Comission.id
	clientId: string;
	clientName: string;
	clientDocument: string;
	reference: string;
	status: string; // status do Comission ('open' | 'paid' | ...), não confundir com o filtro ready/sent/paid
	gateway?: string;
	livveOrderId?: string;
	livveBoletoUrl?: string;
	livveOrderStatus?: string; // "Pago" | "Não Pago" | "Cancelado"
	totValue: string;
	dueDate: string;
	paymentDate?: string | null;
};

export type TLivveInvoiceTotalCount = {
	data: LivveInvoiceRow[];
	totalCount: number;
};

// Estado de exibição derivado — o backend não devolve um "status" pronto pra UI (row.status é o
// status do Comission, ex. 'open'/'paid'), então derivamos a partir de livveOrderId + status.
// Compartilhado entre TableLivve e LivveMenu.
export type LivveUiStatus = "ready" | "sent" | "paid";

export const deriveLivveUiStatus = (row: LivveInvoiceRow): LivveUiStatus => {
	if (row.status === "paid") return "paid";
	if (row.livveOrderId) return "sent";
	return "ready";
};

// getLivveInvoices — GET /comission/livve confirmado pelo backend (manual v2.1, seção 2.5). Sem
// busca por nome (não suportado) — filtra por clientId e/ou reference (ciclo).
const getLivveInvoices = async (
	status: LivveInvoiceStatus | "",
	page = 1,
	limit = 10,
	clientId = "",
	reference = ""
): Promise<TLivveInvoiceTotalCount | Error> => {
	try {
		const { data } = await Api().get(
			`/comission/livve?status=${status}&_page=${page}&_limit=${limit}&clientId=${clientId || "todos"}&reference=${reference || "todos"}`
		);
		if (data.results) {
			return { data: data.results, totalCount: data.total };
		}
		return new Error("Erro ao buscar faturas Livve.");
	} catch (error) {
		console.error(error);
		return new Error((error as { message: string }).message || "Erro ao buscar faturas Livve.");
	}
};

export type LivveGenerateOrderResult = {
	message: string;
	comissions: { id: string; clientId: string; reference: string; livveOrderId: string; livveBoletoUrl: string }[];
	skipped: { id: string; clientId: string; reference: string; livveOrderId: string; livveBoletoUrl: string; reason: string }[];
};

// sendToLivve — atalho por fatura confirmado pelo backend (manual v2.1, seção 2.2): equivale a
// generate-order com { ids: [id] }. Protegido contra duplicidade — se a fatura já tem pedido, ela
// volta em `skipped`, não gera um segundo boleto.
const sendToLivve = async (id: string): Promise<LivveGenerateOrderResult | Error> => {
	try {
		const { data } = await Api().post(`/comission/${id}/livve/send`);
		return data;
	} catch (error) {
		console.error(error);
		return new Error((error as { message: string }).message || "Erro ao enviar a fatura pra Livve.");
	}
};

export type LivveConfirmPaymentResult =
	| { message: string; status: string; operationStatus: string } // boleto ainda não pago
	| { message: string; itemsProcessed: number; details: { id: string; clientId: string; pointsCredited: number }[] }; // pago, pontos creditados

// confirmLivvePayment — endpoint real confirmado pelo manual do backend (POST
// /comission/:id/livve/confirm-payment, corpo vazio) — diferente do que eu tinha suposto
// (reaproveitar PATCH /comission/payment/:id). É idempotente (pode chamar várias vezes).
const confirmLivvePayment = async (comissionId: string): Promise<LivveConfirmPaymentResult | Error> => {
	try {
		const { data } = await Api().post(`/comission/${comissionId}/livve/confirm-payment`, {});
		return data;
	} catch (error) {
		console.error(error);
		return new Error((error as { message: string }).message || "Erro ao confirmar o pagamento.");
	}
};

const getComissionByProposalID = async (id: string): Promise<TComissionStatusTotalCount | Error> => {
	try {
		const { data } = await Api().get(`/comission/proposal/${id}`);
		if (data.results) {
			return { data: data.results, totalCount: data.total };
		}
		return new Error("Erro ao buscar valores");
	} catch (error) {
		console.error(error);
		return new Error("Erro ao buscar valores");
	}
};

export const ComissionServices = {
	getComission,
	getComissionByID,
	getComissionValues,
	getComissionDetail,
	getComissionValuesDetail,
	addValues,
	uploadComissionDocument,
	cancelValuesComission,
	getComissionDocuments,
	removeDocumentByComission,
	getComissionPdf,
	updateValuesComission,
	getComissionByProposalID,
	closeCycle,
	undoCloseCycle,
	getLivveInvoices,
	sendToLivve,
	confirmLivvePayment,
};
